import React from 'react';
import { filter, has, includes, isEmpty, isNil, map, reject } from 'lodash';

import { Delete as DeleteIcon, People as PeopleIcon } from '@mui/icons-material';
import { CircularProgress, Stack } from '@mui/material';

import {
  baseSelectStyles,
  getCurrentSelectedOption,
  getNewSelectionValue,
  SelectInput,
  SelectOption,
} from '../rselect/ReactSelectUtils';
import UnsortedSelect from '../rselect/UnsortedSelect';
import SortedSelect from '../rselect/SortedSelect';
import Button from '../editors/mui/Button';
import { getEffectiveRole } from '../../../lib/ACLUtils';
import { isFlowNodeResource } from '../../../lib/ResourceUtils';
import { isValidEmail } from '../../../lib/ValidationUtils';
import {
  ACL_RESOURCE_TYPE_CREDENTIALS,
  ACL_RESOURCE_TYPE_DATASET,
  ACL_RESOURCE_TYPE_DF_DATASET,
  ACL_RESOURCE_TYPE_DF_SINK,
  ACL_RESOURCE_TYPE_DF_SOURCE,
  ACL_RESOURCE_TYPE_FLOW,
  ACL_RESOURCE_TYPE_LOOKUPS,
  ACL_RESOURCE_TYPE_PROJECTS,
  ACL_RESOURCE_TYPE_TRANSFORM,
} from '../../../constants/aclConstants';
import {
  RESOURCE_TYPE_DATA_SET,
  RESOURCE_TYPE_SINK,
  RESOURCE_TYPE_SOURCE,
} from '../../../constants/resourceTypes';

import FlowPreviewContainer from '../../sendData/summaryViews/FlowPreviewContainer';
import ZoomControl from '../zoomControl/ZoomControl';

import './AclTabView.scss';
import flowTypes from 'new/components/flows/types/flowTypes';
import { Banner } from 'new/elements/Banner/Banner';
import { flowDataMap } from 'new/elements/flows/dataMap';
import { capitalizeString } from 'new/utils/StringUtil/capitalizeArrayOfString';
import { TransferFlowOwnershipDialog } from 'new/components/collaborate/TransferFlowOwnership/TransferFlowOwnershipDialog';

const styles = {
  titleIcon: {
    display: 'flex',
    alignItems: 'center',
    marginRight: 10,
  },
  buttonStyle: {
    width: 'auto',
    height: '100%',
    lineHeight: '30px',
    marginLeft: 10,
    marginRight: 10,
    padding: '0 6px',
  },
};

const ACL_SETTINGS_MAPPING = {
  collaborator: {
    key: 'collaborator',
    label: 'Collaborator (Read-only)',
    value: 'collaborator',
    description:
      'Able to view all information about this resource and use it in other Flows or Nexsets, but should not be able to modify it.',
  },
  operator: {
    key: 'operator',
    label: 'Operator',
    value: 'operator',
    description: 'Same as collaborator, but can also pause/activate resources.',
  },
  admin: {
    key: 'admin',
    label: 'Administrator',
    value: 'admin',
    description: 'Full administrative rights to this resource.',
  },
};

const ACL_SETTINGS_OPTIONS = [
  ACL_SETTINGS_MAPPING['collaborator'],
  ACL_SETTINGS_MAPPING['operator'],
  ACL_SETTINGS_MAPPING['admin'],
];

// Offered on flows only, to the flow's owner, and only for a single user:
// ownership can't go to a team or the entire organization.
const ACL_OWNER_OPTION = {
  key: 'owner',
  label: 'Owner',
  value: 'owner',
  description: 'Transfer ownership of this flow to the selected user.',
};

const aclItemRoleSelectStyles = {
  ...baseSelectStyles,
  container: (base) => ({
    ...base,
    width: 180,
    marginRight: 8,
  }),
};

class AclTabView extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      aclFormResourceType: 'USER',
      aclFormResourceId: null,
      aclFormUserInput: '',
      aclFormRoleSelection: 'collaborator',
      shareFormResourceType: 'USER',
      shareFormResourceId: null,
      shareFormUserInput: '',
      activePanelKey: 0,
      scaleFactor: 0.8,
      isTransferOwnershipOpen: false,
      transferOwnershipTarget: null,
    };
  }

  componentDidMount() {
    this.props.fetchAcl(this.props.resourceType, this.props.data);

    if (this._isNexsetSharing()) {
      this.props.fetchAcl(ACL_RESOURCE_TYPE_DATASET, this.props.data);
    }

    if (
      has(this.props, 'userOrg.id') &&
      (isNil(this.props.orgMembership) || !isNil(this.props.orgMembership.lastFetched))
    ) {
      this.props.fetchOrgMembers(this.props.userOrg.id);
    }

    this.props.loadMyTeams();
  }

  componentWillUnmount() {
    this.props.clearAcl();
  }

  render() {
    const {
      data,
      flow,
      aclItems,
      datasetAclItems,
      resourceType,
      readOnly,
      isSaving,
      isLoading,
      isFlow,
    } = this.props;
    const { scaleFactor, isTransferOwnershipOpen, transferOwnershipTarget } = this.state;
    const flowType = data?.flow_type || flow?.flow_type;
    const isDataset = this._isNexsetSharing();

    // disallow sharing for Nexsets that are not from streaming flows
    if (isDataset && flowType !== flowTypes.streaming) {
      const flowTypeName = flowDataMap[flowType]?.name || flowType;
      const capitalizedName = capitalizeString(flowTypeName);
      const message = `${capitalizedName} Flows Nexsets cannot be shared.`;
      return (
        <Stack padding={2}>
          <Banner variant="default" message={message} />
        </Stack>
      );
    }

    if (isLoading === true) {
      return (
        <div className="tab-content acl-tab">
          <section style={{ width: '100%', height: '100%' }}>
            <header>
              <span style={styles.titleIcon}>
                <PeopleIcon
                  fontSize="large"
                  htmlColor="#2A3F56"
                  style={{ width: 20, height: 20 }}
                />
              </span>
              <h3>Access Management</h3>
            </header>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 350,
              }}
            >
              <CircularProgress color="secondary" />
            </div>
          </section>
        </div>
      );
    }

    const resourceTypeName = this._getResourceTypeName();

    const nodeKey = this._getNodeKey();
    let flowGraphElementHeight = this._flowGraphElement ? this._flowGraphElement.offsetHeight : 0;

    const aclIntroRow = (
      <div className="acl-info-row" key="acl-intro-row" style={{ textAlign: 'center' }}>
        {!isDataset && (
          <h3 style={{ fontWeight: 'bold' }}>
            {readOnly ? (
              `You have read-only permissions for this ${resourceTypeName}.`
            ) : (
              <span>Invite Users to Collaborate on this {resourceTypeName}</span>
            )}
          </h3>
        )}
        <p style={{ fontSize: 'var(--font-size-base-sm)' }}>
          {resourceTypeName} owners and administrators can invite other users, teams, or entire
          organization to collaborate on this {resourceTypeName}
        </p>
        {resourceType === ACL_RESOURCE_TYPE_FLOW && (
          <div className="flow-preview-container">
            <div className="zoom-container">
              <ZoomControl
                scaleFactor={scaleFactor}
                minScaleFactor={0.1}
                maxScaleFactor={1}
                stepSize={0.1}
                onScaleFactorChanged={this._handleScaleFactorChanged}
              />
            </div>
            <div className="source-pipeline-container" style={{ height: flowGraphElementHeight }}>
              <FlowPreviewContainer
                nodeKey={nodeKey}
                scaleFactor={scaleFactor}
                onRootRef={(instance) => {
                  if (instance && instance !== this._flowGraphElement) {
                    this._flowGraphElement = instance;
                  }
                }}
              />
            </div>
          </div>
        )}
      </div>
    );

    const aclInvitationRow = !readOnly ? this._renderAclInvitationForm() : null;

    return (
      <div className="tab-content acl-tab">
        {isSaving && (
          <div className="acl-saving-indicator">
            <CircularProgress size={80} thickness={6} />
          </div>
        )}
        <section>
          <header>
            <span style={styles.titleIcon}>
              <PeopleIcon fontSize="large" htmlColor="#2A3F56" style={{ height: 20, width: 20 }} />
            </span>
            <h3>Access Management</h3>
          </header>
          {isDataset ? (
            <div id="acl-tab-accordion" className="accordion-group">
              <div className="accordion-body-content acl-info-container">
                {!readOnly && this._renderAclInvitationForm(true)}
                {map(
                  filter(datasetAclItems, (aclItem) => getEffectiveRole(aclItem) === 'sharer'),
                  (a, idx) => this._renderAclItem(a, idx),
                )}
                {this._renderExternalSharersList()}
              </div>
            </div>
          ) : (
            <div className="acl-info-container">
              {aclIntroRow}
              {aclInvitationRow}
              {isFlow && this._renderOwnerRow()}
              {map(
                // The owner's row above replaces any accessor entry they also hold,
                // e.g. the admin grant made before a transfer.
                isFlow
                  ? reject(
                      aclItems,
                      (a) => a.type === 'USER' && a.id === this._getResourceOwnerId(),
                    )
                  : aclItems,
                (a, idx) => this._renderAclItem(a, idx),
              )}
            </div>
          )}
        </section>
        {isFlow && (
          <TransferFlowOwnershipDialog
            open={isTransferOwnershipOpen}
            newOwnerName={transferOwnershipTarget?.name ?? ''}
            onClose={this._handleCloseTransferOwnership}
            onConfirm={this._handleTransferOwnership}
          />
        )}
      </div>
    );
  }

  // A flow that starts from a Nexset shares its resource type, but its Access tab
  // manages the flow's accessors, not Nexset sharing.
  _isNexsetSharing = () => {
    const { resourceType, isFlow } = this.props;

    return resourceType === ACL_RESOURCE_TYPE_DF_DATASET && !isFlow;
  };

  _handleScaleFactorChanged = (scaleFactor) => {
    this.setState({ scaleFactor });
  };

  _getNodeKey = () => {
    const {
      data: { id },
    } = this.props;
    let flowResourceType = this._getResourceType();

    switch (flowResourceType) {
      case RESOURCE_TYPE_SOURCE:
        return `SOURCE_${id}`;

      case RESOURCE_TYPE_DATA_SET:
        return `DATA_SET_${id}`;

      case RESOURCE_TYPE_SINK:
        return `DATA_SINK_${id}`;

      default:
    }
  };

  _renderAclItem = (aclItem = null, idx) => {
    const { orgs, orgMembership, readOnly } = this.props;

    if (!aclItem) {
      return <div key={idx} />;
    }

    const { id, type } = aclItem;
    let aclItemName = '';

    switch (type) {
      case 'USER':
        aclItemName =
          id && orgMembership && orgMembership.membersById && orgMembership.membersById[id] ? (
            <div>
              {orgMembership.membersById[id].full_name}
              <br />
              <span style={{ fontSize: 'var(--font-size-sm)' }}>{aclItem.email}</span>
            </div>
          ) : (
            <div>{aclItem.email}</div>
          );

        break;

      case 'ORG':
        aclItemName = (
          <div>
            Entire organization
            <br />
            <span style={{ fontSize: 'var(--font-size-sm)' }}>
              {id && orgs[id] && `Everyone in ${orgs[id].name} organization`}
            </span>
          </div>
        );

        break;

      case 'TEAM':
        aclItemName = (
          <div>
            {aclItem.name}
            <br />
            <span style={{ fontSize: 'var(--font-size-sm)' }}>
              <b>Type:</b> Team
            </span>
          </div>
        );

        break;
      default:
        break;
    }

    const role = getEffectiveRole(aclItem);
    const aclValue = ACL_SETTINGS_MAPPING[role];
    const canBecomeOwner = type === 'USER' && this._isCurrentUserFlowOwner();

    return (
      <div className="acl-info-row" key={idx}>
        <div className="acl-row-item" style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div className="acl-item-name">{aclItemName}</div>
          <div className="acl-item-right">
            {role !== 'sharer' && (
              <div className="acl-role">
                <UnsortedSelect
                  options={
                    canBecomeOwner
                      ? [...ACL_SETTINGS_OPTIONS, ACL_OWNER_OPTION]
                      : ACL_SETTINGS_OPTIONS
                  }
                  components={{ Input: SelectInput, Option: SelectOption }}
                  isClearable={false}
                  isDisabled={readOnly}
                  backspaceRemovesValue={false}
                  maxMenuHeight={450}
                  value={aclValue}
                  onChange={(value) =>
                    value.value === ACL_OWNER_OPTION.value
                      ? this._handleOpenTransferOwnership({
                          id,
                          name: orgMembership?.membersById?.[id]?.full_name || aclItem.email,
                        })
                      : this._handleAclEditRole(value, aclItem)
                  }
                  styles={aclItemRoleSelectStyles}
                />
              </div>
            )}
            <div className="acl-delete" onClick={() => this._handleDeleteAcl(aclItem)}>
              <DeleteIcon
                fontSize="large"
                htmlColor={readOnly ? '#DFE8F0' : '#FF7436'}
                style={{ cursor: readOnly ? 'not-allowed' : 'pointer' }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  _renderOwnerRow = () => {
    const owner = this._getResourceOwner();

    if (!owner) return null;

    return (
      <div className="acl-info-row" key="acl-owner">
        <div className="acl-row-item" style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div className="acl-item-name">
            <div>
              {owner.full_name}
              <br />
              <span style={{ fontSize: 'var(--font-size-sm)' }}>{owner.email}</span>
            </div>
          </div>
          <div className="acl-item-right">
            <div className="acl-role">
              <UnsortedSelect
                options={[ACL_OWNER_OPTION]}
                components={{ Input: SelectInput, Option: SelectOption }}
                isDisabled
                value={ACL_OWNER_OPTION}
                styles={aclItemRoleSelectStyles}
              />
            </div>
            {/* The owner can't be removed; the hidden icon keeps the role column aligned. */}
            <div className="acl-delete" style={{ visibility: 'hidden' }}>
              <DeleteIcon fontSize="large" />
            </div>
          </div>
        </div>
      </div>
    );
  };

  _renderAclInvitationForm = (isShareForm = false) => {
    const { aclFormUserInput, aclFormRoleSelection, shareFormUserInput, shareFormResourceType } =
      this.state;
    const { userTeamIds, teams, currentUser, userOrg, orgMembership } = this.props;

    const resourceOwner = this._getResourceOwner() ?? {};

    const userOptions =
      !isNil(orgMembership) && !isNil(orgMembership, 'lastFetched')
        ? map(
            filter(
              orgMembership.membersById,
              (m) =>
                m.id !== currentUser.id &&
                m.id !== resourceOwner?.id &&
                m.email !== 'admin@nexla.com' &&
                m.user_status === 'ACTIVE' &&
                m.org_membership_status === 'ACTIVE',
            ),
            (m) => {
              return {
                label: m.full_name,
                description: m.email,
                value: m.email,
                optype: 'USER',
                res_id: m.id,
              };
            },
          )
        : [];

    const teamOptions = map(userTeamIds, (u) => {
      const thisTeam = teams[u];
      return {
        label: thisTeam.name,
        value: `nex_acl_team.${u}`,
        description: thisTeam.description,
        optype: 'TEAM',
        res_id: u,
      };
    });
    const orgOption = {
      label: `Entire organization`,
      value: `nex_acl_org.${userOrg.id}`,
      description: `Everyone in ${userOrg.name} organization`,
      optype: 'ORG',
      res_id: userOrg.id,
    };
    const memberEmails = map(userOptions, (m) => m.value);
    let isCreatable = false;
    let selectionValues = [orgOption];

    if (isShareForm) {
      isCreatable = true;

      // grouped options and creatable select don't work well together, so no
      // grouping
      selectionValues.push(...userOptions, ...teamOptions);
    } else {
      if (!isEmpty(userOptions)) {
        selectionValues.push({
          label: 'People',
          value: 'People',
          options: userOptions,
        });
      }

      if (!isEmpty(teamOptions)) {
        selectionValues.push({
          label: 'Teams',
          value: 'Teams',
          options: teamOptions,
        });
      }
    }

    if (
      isShareForm &&
      shareFormUserInput &&
      shareFormUserInput !== '' &&
      shareFormResourceType === 'USER' &&
      !includes(memberEmails, shareFormUserInput)
    ) {
      selectionValues = [
        {
          label: shareFormUserInput,
          value: shareFormUserInput,
          optype: 'USER',
          res_id: null,
        },
        ...selectionValues,
      ];
    }

    const userInputCurrentValue = getCurrentSelectedOption(
      selectionValues,
      isShareForm ? shareFormUserInput : aclFormUserInput,
    );

    const roleOptions = this._canTransferOwnership()
      ? [...ACL_SETTINGS_OPTIONS, ACL_OWNER_OPTION]
      : ACL_SETTINGS_OPTIONS;
    const roleInputCurrentValue = getCurrentSelectedOption(roleOptions, aclFormRoleSelection);
    const isTransferOwnership = aclFormRoleSelection === ACL_OWNER_OPTION.value;

    return (
      <div className="acl-info-row" key="acl-invitation" style={{ backgroundColor: '#F5F8FA' }}>
        <div className="acl-invitation-form acl-row-item">
          <SortedSelect
            isCreatable={isCreatable}
            formatCreateLabel={(s) => `Invite ${s}`}
            placeholder={isCreatable ? 'Type to select or create...' : 'Type to select...'}
            styles={{
              ...baseSelectStyles,
              container: (base) => ({
                ...base,
                flex: 1,
                maxWidth: 400,
              }),
            }}
            options={selectionValues}
            components={{ Input: SelectInput, Option: SelectOption }}
            isClearable={false}
            isSearchable={true}
            backspaceRemovesValue={false}
            name="select"
            value={userInputCurrentValue}
            onChange={isShareForm ? this._handleShareCreateUser : this._handleAclCreateUser}
          />

          {isShareForm ? (
            <div className="acl-item-right">
              <Button
                buttonTheme="secondary"
                rootStyle={styles.buttonStyle}
                onClick={this._handleShare}
              >
                Share Data
              </Button>
            </div>
          ) : (
            <div className="acl-item-right">
              <UnsortedSelect
                options={roleOptions}
                components={{ Input: SelectInput, Option: SelectOption }}
                isClearable={false}
                backspaceRemovesValue={false}
                // The default 300px cuts off Owner; the menu still only grows to fit its options.
                maxMenuHeight={450}
                value={roleInputCurrentValue}
                onChange={this._handleAclCreateRole}
                styles={{
                  ...baseSelectStyles,
                  container: (base) => ({
                    ...base,
                    width: 180,
                    marginLeft: 10,
                  }),
                }}
              />

              <Button
                buttonTheme="secondary"
                rootStyle={styles.buttonStyle}
                onClick={
                  isTransferOwnership ? this._handleOpenFormTransferOwnership : this._handleSetAcl
                }
              >
                {isTransferOwnership ? 'Transfer Ownership' : 'Grant Access'}
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  };

  _renderExternalSharersList = () => {
    const {
      data: { external_sharers },
    } = this.props;

    if (isEmpty(external_sharers)) return null;

    return [
      <div key="external-sharers-heading-row" className="acl-info-row external-sharers-heading-row">
        <h3>Invited to join Nexla</h3>
        <p>
          These users do not have a Nexla account. This dataset will be available to them once they
          have an account.
        </p>
      </div>,
      ...map(external_sharers, (sharer) => (
        <div className="acl-info-row" key={sharer.email}>
          <div
            className="acl-row-item"
            style={{ display: 'flex', justifyContent: 'space-between' }}
          >
            <div className="acl-item-name">{sharer.email}</div>
            <div className="acl-item-right">
              <div className="acl-delete" onClick={() => this._handleDeleteSharer(sharer.email)}>
                <DeleteIcon fontSize="large" htmlColor="#FF7436" style={{ cursor: 'pointer' }} />
              </div>
            </div>
          </div>
        </div>
      )),
    ];
  };

  _getResourceTypeName = () => {
    const { resourceType, isFlow } = this.props;
    let type = isFlow ? ACL_RESOURCE_TYPE_FLOW : resourceType;

    switch (type) {
      case ACL_RESOURCE_TYPE_PROJECTS:
        return 'Project';
      case ACL_RESOURCE_TYPE_DF_SOURCE:
        return 'Source';

      case ACL_RESOURCE_TYPE_DF_SINK:
        return 'Destination';

      case ACL_RESOURCE_TYPE_DF_DATASET:
        return 'Dataset';

      case ACL_RESOURCE_TYPE_TRANSFORM:
        return 'Transform';

      case ACL_RESOURCE_TYPE_CREDENTIALS:
        return 'Credential';

      case ACL_RESOURCE_TYPE_LOOKUPS:
        return 'Lookup';

      case ACL_RESOURCE_TYPE_FLOW:
        return 'Flow';

      default:
        return 'Resource';
    }
  };

  _getResourceOwnerId = () => {
    const { data } = this.props;

    return data?.owner_id ?? data?.owner?.id ?? data?.owner;
  };

  _getResourceOwner = () => {
    const { orgMembership, ownerUser } = this.props;

    // Fall back to the normalized user for owners not listed as org members.
    return orgMembership?.membersById?.[this._getResourceOwnerId()] ?? ownerUser ?? null;
  };

  _handleAclCreateUser = (value) => {
    const newUser = getNewSelectionValue(value, true);
    const resourceType = !isNil(value.optype) ? value.optype : 'USER';

    this.setState(({ aclFormRoleSelection }) => ({
      aclFormUserInput: newUser,
      aclFormResourceType: resourceType,
      aclFormResourceId: !isNil(value.res_id) ? value.res_id : null,
      // Owner is not offered for teams or the org, so don't carry it over.
      aclFormRoleSelection:
        resourceType !== 'USER' && aclFormRoleSelection === ACL_OWNER_OPTION.value
          ? ACL_SETTINGS_MAPPING.collaborator.value
          : aclFormRoleSelection,
    }));
  };

  // Only the flow's owner may hand it over, even though admins can manage it.
  _isCurrentUserFlowOwner = () => {
    const { isFlow, currentUser } = this.props;
    const ownerId = this._getResourceOwnerId();

    return isFlow && !isNil(ownerId) && currentUser?.id === ownerId;
  };

  _canTransferOwnership = () => {
    const { aclFormResourceType, aclFormResourceId } = this.state;

    return (
      this._isCurrentUserFlowOwner() && aclFormResourceType === 'USER' && !isNil(aclFormResourceId)
    );
  };

  _getSelectedUserName = () => {
    const { orgMembership } = this.props;
    const { aclFormResourceId, aclFormUserInput } = this.state;

    return orgMembership?.membersById?.[aclFormResourceId]?.full_name || aclFormUserInput;
  };

  // The target is captured so the dialog keeps its text while the form resets.
  _handleOpenTransferOwnership = (transferOwnershipTarget) => {
    this.setState({ isTransferOwnershipOpen: true, transferOwnershipTarget });
  };

  _handleOpenFormTransferOwnership = () => {
    this._handleOpenTransferOwnership({
      id: this.state.aclFormResourceId,
      name: this._getSelectedUserName(),
    });
  };

  _handleCloseTransferOwnership = () => {
    this.setState({ isTransferOwnershipOpen: false });
  };

  _handleTransferOwnership = async ({ includeDataCredentials, includeReusableCode }) => {
    const { data, resourceType, userOrg, transferFlowOwnership, showAlert } = this.props;
    const { transferOwnershipTarget } = this.state;
    const isFlowNode = isFlowNodeResource(resourceType, data);

    // Rejects, keeping the dialog open, if either call fails.
    await transferFlowOwnership({
      flowId: data.flow_node_id ?? (isFlowNode ? data.id : undefined),
      newOwnerId: transferOwnershipTarget.id,
      newOrgId: userOrg.id,
      includeDataCredentials,
      includeReusableCode,
      aclResourceType: resourceType,
      resourceId: data.id,
    });

    // The caller is no longer the owner, so Owner can't stay selected in the form,
    // and the new owner drops out of its user list.
    this.setState({
      aclFormUserInput: '',
      aclFormResourceType: 'USER',
      aclFormResourceId: null,
      aclFormRoleSelection: ACL_SETTINGS_MAPPING.collaborator.value,
    });
    showAlert({
      title: `Flow ownership transferred to ${transferOwnershipTarget.name}.`,
      severity: 'success',
    });
  };

  _handleAclCreateRole = (value) => {
    const newRole = getNewSelectionValue(value, true);
    this.setState({
      aclFormRoleSelection: newRole,
    });
  };

  _getResourceType = () => {
    const { resourceType } = this.props;
    let flowResourceType;

    switch (resourceType) {
      case ACL_RESOURCE_TYPE_DF_SOURCE:
        flowResourceType = RESOURCE_TYPE_SOURCE;
        break;

      case ACL_RESOURCE_TYPE_DF_SINK:
        flowResourceType = RESOURCE_TYPE_SINK;
        break;

      case ACL_RESOURCE_TYPE_DF_DATASET:
        flowResourceType = RESOURCE_TYPE_DATA_SET;
        break;

      default:
    }

    return flowResourceType;
  };

  _handleAclEditRole = (value, aclItem) => {
    const { data, resourceType, updateAcl } = this.props;

    updateAcl(resourceType, data, aclItem.type, aclItem.id, value.value);
  };

  _handleShareCreateUser = (value) => {
    const newUser = getNewSelectionValue(value, true);

    this.setState({
      shareFormUserInput: newUser,
      shareFormResourceType: !isNil(value.optype) ? value.optype : 'USER',
      shareFormResourceId: !isNil(value.res_id) ? value.res_id : null,
    });
  };

  _handleSetAcl = () => {
    const { aclFormResourceType, aclFormRoleSelection, aclFormResourceId } = this.state;
    const { data, resourceType, setError, updateAcl } = this.props;

    if (!aclFormResourceId) {
      setError({
        message: 'Please select a user, a team, or the entire organization.',
      });
    } else {
      updateAcl(resourceType, data, aclFormResourceType, aclFormResourceId, aclFormRoleSelection);
    }
  };

  _handleDeleteAcl = (aclItem) => {
    const { data, resourceType, updateAcl, readOnly } = this.props;

    if (readOnly) return;

    let role = null;
    const effRole = getEffectiveRole(aclItem);
    if (!!effRole && effRole === 'sharer') role = effRole;
    updateAcl(resourceType, data, aclItem.type, aclItem.id, effRole, true);
  };

  _handleShare = () => {
    const { shareFormResourceType, shareFormUserInput, shareFormResourceId } = this.state;
    const { data, resourceType, setError, updateAcl, shareDatasetAndFetch } = this.props;
    const isExternalUser = !shareFormResourceId && isValidEmail(shareFormUserInput);

    if (isExternalUser) {
      shareDatasetAndFetch(data.id, shareFormUserInput);
    } else if (!shareFormResourceId) {
      setError({
        message: isExternalUser
          ? 'Users not in your organization can only be granted data sharer access.'
          : 'Please select a user, a team, or the entire organization.',
      });
    } else {
      updateAcl(resourceType, data, shareFormResourceType, shareFormResourceId, 'sharer');
    }
  };

  _handleDeleteSharer = (email) => {
    const {
      data: { id },
      unshareDatasetAndFetch,
    } = this.props;

    unshareDatasetAndFetch(id, email);
  };
}

AclTabView.defaultProps = {
  isFlow: false,
};

export default AclTabView;
